<template>
  <div class="content">
    <router-view v-if="!loading" />
    <SwdFullScreenLoading v-else />
    <AutoLogout />
    <ModalReloadPage />
    <SwdShareDialog />
    <SwdModalExportSucces />
    <ChooseAccount />
    <SwdModalTerms />
    <SwdPreviewModal />
    <SwdModalSummaryInfo />
    <VueQueryDevTools />
    <FloatingPopup />
  </div>
</template>

<script>
import { onMounted, computed, onBeforeMount } from 'vue'
import { useStore } from 'vuex'
import { useQueryProvider } from 'vue-query'
import { VueQueryDevTools } from 'vue-query/devtools'
import { tokenStorage } from './api/api-client/TokenStorage'
import FloatingPopup from './components/Global/FloatingPopup.vue'
import { useSockets } from './sockets/use-sockets'
import ModalReloadPage from '@/components/ModalReloadPage/ModalRealoadPage.vue'
import SwdShareDialog from '@/components/Global/SwdShareDialog.vue'
import SwdModalTerms from '@/components/Global/SwdModalTerms.vue'
import SwdPreviewModal from '@/components/Global/SwdPreviewModal.vue'
import SwdModalExportSucces from '@/components/Documents/ClientReport/ModalExportSucces.vue'
import SwdModalSummaryInfo from '@/components/SummaryInfo/SwdModalSummaryInfo.vue'
import { useRouter, useRoute } from 'vue-router'
import AutoLogout from './views/AutoLogout.vue'
import ChooseAccount from '@/components/Global/ChooseAccount.vue'
import { fetchRoles } from './api/vueQuery/fetch-roles'
import { groupPermissionsByRole } from '@/utils/permission-helpers'
import { removeFromStorage } from '@/utils/utilsSessionStorage'
import { handleSecureUploadRedirect } from '@/router/modules/secureUploadRedirect'

export default {
  components: {
    VueQueryDevTools,
    ModalReloadPage,
    SwdShareDialog,
    SwdModalExportSucces,
    ChooseAccount,
    SwdModalTerms,
    SwdPreviewModal,
    SwdModalSummaryInfo,
    AutoLogout,
    FloatingPopup,
  },
  setup() {
    useQueryProvider({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
        },
      },
    })

    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const loading = computed(() => {
      return store.state.globalComponents.isLoadingApp
    })

    // Check authentication status based on Vuex store
    const isAuthenticated = computed(() => store.state.auth.isAuth)
    const userId = store.state.globalComponents.userId

    const isUserLoggedIn = computed(() => userId !== null)

    onBeforeMount(async () => {
      const access_token = tokenStorage.getByKey('access_token')
      if (JSON.parse(tokenStorage.getByKey('refresh_token_expired'))) {
        sessionStorage.clear()
      }
      await router.isReady()

      const excludedRoutesForAuth = [
        'account-verification',
        'secure-upload',
        'passwordcreate',
        'passwordreset',
        'secure-upload-dashboard',
        'secure-upload-medicare-details',
        'secure-upload',
        'forgotpassword',
      ]
      //force fully redirect to login if unauthorized
      if (access_token === null && !excludedRoutesForAuth.includes(route.name)) {
        removeFromStorage(sessionStorage, 'access_token')
        removeFromStorage(sessionStorage, 'otp-type')
        removeFromStorage(sessionStorage, 'otp_verified')
        removeFromStorage(sessionStorage, 'refresh_token_expired')
        await store.dispatch('globalComponents/resetState')
        store.commit('auth/setAuthUser', false)
        sessionStorage.clear()
        router.push({ path: '/login' })
      }
    })

    onMounted(async () => {
      const auth = JSON.parse(tokenStorage.getByKey('auth'))
      const otp_verified = JSON.parse(tokenStorage.getByKey('otp_verified'))
      const otpType = JSON.parse(tokenStorage.getByKey('otp-type'))
      const access_token = tokenStorage.getByKey('access_token')
      const otpEnabled = sessionStorage.getItem('otp-enabled')
      const excludedRoutesForAuth = ['account-verification', 'secure-upload', 'passwordcreate', 'passwordreset', '503']

      await router.isReady()
      if (!excludedRoutesForAuth.includes(route.name)) {
        const redirect = handleSecureUploadRedirect(route.name)
        if (redirect) return redirect
        if (
          access_token !== null &&
          auth &&
          auth.isAuth !== null &&
          (otp_verified === true || otpEnabled === 'false')
        ) {
          const { multipleAccount, termsAndConditions, isMultipleVerified } = store.state.globalComponents
          if (multipleAccount.length > 1 && termsAndConditions && !isMultipleVerified) {
            router.push({ name: 'choose_account' })
            return
          }

          store.commit('auth/setAuthUser', true)
          useSockets()
          if (route?.params?.p) {
            router.push({ path: route.path, params: { p: route.params.p } })
          } else {
            router.push({ path: route.path })
          }
        } else if (access_token !== null && otp_verified === false && otpType !== null) {
          store.commit('auth/setAuthUser', false)
          const otpType = tokenStorage.getByKey('otp-type')
          store.commit('auth/setOtpType', otpType)
          router.push({ name: 'otp' })
        } else {
          store.commit('auth/setAuthUser', false)
          tokenStorage.setByKey('otp_verified', false)
          router.push({ path: '/login' })
        }
      }
    })
    return {
      loading,
      isAuthenticated,
      isUserLoggedIn,
    }
  },
  async mounted() {
    if (this.$store.state.auth.isAuth) {
      this.$store.commit('globalComponents/setIsLoadingApp', true)
      await this.getPermissions()
      this.$store.commit('globalComponents/setIsLoadingApp', false)
    }
  },
  methods: {
    async getPermissions() {
      if (!this.$store.state.auth.isAuth) {
        return
      }
      const permissions = await fetchRoles()
      if (!permissions || !permissions.data) {
        return
      }
      const groupedRoles = groupPermissionsByRole(permissions.data)
      this.$store.commit('globalComponents/setPermissions', groupedRoles)
    },
  },
}
</script>

<style lang="css">
@import '~element-plus/dist/index.css';
@import '~element-plus/theme-chalk/display.css';
@import '~cropperjs/dist/cropper.css';
</style>
